<template>
    <div class="border bg-gray-100 rounded p-4 mt-3">
        <div class="mb-4">Comparison Elements</div>
        <div class="w-full">
            <div v-for="(header, index) in data.headers"
                 :key="`comparisonElementHeaders-${index}`"
                 class="relative mt-3 max-w-12xl item">
                    <div class="flex items-center">
                        <input-text
                            name="main_text"
                            label="Header"
                            class="mb-3"
                            v-model="header.main_text"
                            :maxlength=255
                        />
                        <div
                            @click="deleteHeader(index)"
                            class="ml-2 cursor-pointer"
                        >
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </div>
                    </div>
            </div>
            <button slot="footer"
                class="mt-6 text-indigo-600 hover:text-indigo-900"
                type="button"
                @click="AddHeader"
            >
                + Add header
            </button>
            <div v-for="(block, index) in data.blocks"
                 :key="`comparisonElementBlocks-${index}`"
                 class="relative mt-3 max-w-12xl item">
                <div class="flex items-center">
                    <media-reference
                        :name="`comparison_elements_[${index}][image]`"
                        v-model="block.image"
                        :language="language"
                        class="ml-3"
                        @input="updateImage(index, $event)"
                    />
                    <input-text
                        name="cta_text"
                        label="CTA Text"
                        class="mb-3 ml-3"
                        v-model="block.cta_text"
                        :maxlength=255
                    />
                    <Select
                            label="Editor's Pick"
                            name="picked"
                            v-model="block.picked"
                            class="ml-3 -mt-3  w-60"
                            :options="[
                                { value: '1', name: 'Yes' },
                                { value: '0', name: 'No' },
                            ]"
                            @input="pickChanged($event, index)"
                        />

                    <reference
                        create-text="Create new category and link category to this page"
                        link-text="Link an existing category"
                        :two-step-create="true"
                        :creatable="false"
                        :language="language"
                        v-model="block.category"
                        model-data="All Category"
                        :query="{
                            status_id: 1,
                        }"
                        :pre-filled="block.category || null"
                        :order="{
                            'categories.name': 'asc'
                        }"
                        @input="updateCategory(index, $event)"
                    />
                    <div
                        @click="deleteBlock(index)"
                        class="ml-2 cursor-pointer"
                    >
                        <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </div>
                </div>
                <div v-for="(answer, i) in block.answers" :key="`comparisonElementBlockAnswers-${i}`" class="relative mt-3 max-w-12xl item">
                    <div class="flex items-center">
                        <Select
                            label="Type"
                            name="type"
                            v-model="answer.type"
                            class="ml-3 -mt-3  w-60"
                            :options="[
                                { value: 'Freetext', name: 'Freetext' },
                                { value: 'Availability', name: 'Availability' },
                            ]"
                            @input="typeChanged($event, i, index)"
                        />

                        <input-text
                            v-if="answer.type === 'Freetext'"
                            name="text"
                            label="Text"
                            class="mb-3 ml-3"
                            v-model="answer.text"
                            :maxlength=255
                        />

                        <Select
                            v-if="answer.type === 'Availability'"
                            label="Availability"
                            name="availability"
                            v-model="answer.availability"
                            class="ml-3 -mt-3  w-60"
                            :options="[
                                { value: '1', name: 'Yes' },
                                { value: '0', name: 'No' },
                            ]"
                            @input="availabilityChanged($event, i, index)"
                        />
                    </div>
                </div>
            </div>
            <button slot="footer"
                class="mt-6 text-indigo-600 hover:text-indigo-900"
                type="button"
                @click="AddBlock"
            >
                + Add block
            </button>
        </div>
    </div>
</template>

<script>
import siteLanguage from '../../config/siteLanguage'

export default {
    name: "ComparisonElements",
    props: {
        value: [Object, Array],
        contentBlockId: Number,
        contentSectionId: Number,
        language: {
            type: Object,
            default: () => {
                return { iso: siteLanguage.iso.ENGLISH };
            },
        },
    },
    data() {
        return {
            data: this.value?.id ?  this.value : this.defaultData(),
        };
    },
    updated() {
        this.$emit("input", this.data);
    },
    methods: {
        defaultData() {
            return {
                headers: [this.defaultHeader()],
                blocks: [this.defaultBlock()]
            }
        },
        defaultHeader() {
            return {
                main_text: ""
            }
        },
        defaultBlock() {
            return {
                image: null,
                category: null,
                category_id: null,
                image_id: null,
                cta_text: "",
                picked: "0",
                answers: [this.defaultAnswer()]
            }
        },
        defaultAnswer() {
            return {
                type: "Freetext",
                availability: null,
                text: ""
            }
        },
        AddHeader(e, index) {
            e.preventDefault();
            this.data.headers.push(this.defaultHeader());
            this.data.blocks = this.data.blocks.map(item => {
                item.answers.push(this.defaultAnswer());
                 return item; });
        },
        AddBlock(e, index) {
            e.preventDefault();
            if (this.data.blocks.length == 4) {
                alert("You can add maximum 4 blocks");
            } else {
                this.data.blocks.push(this.defaultBlock());
                for (var i = 1; i < this.data.headers.length; i++) {
                    this.data.blocks[this.data.blocks.length-1].answers.push(this.defaultAnswer());
                }
            }
        },
        AddAnswer(e, index) {
            this.data[index].answers.push(this.defaultAnswer());
        },
        deleteHeader: function (indexOfItemToDelete) {
            if (confirm('Are you sure you want to remove this header?')) {
                this.data.headers.splice(indexOfItemToDelete, 1);
                this.data.blocks = this.data.blocks.map(item => { item.answers.splice(indexOfItemToDelete, 1); return item; });
            }
        },
        deleteBlock: function (indexOfItemToDelete) {
            if (confirm('Are you sure you want to remove this block?')) {
                this.data.blocks.splice(indexOfItemToDelete, 1);
            }
        },
        handleDelete: function (indexOfItemToDelete) {
            if (confirm('Are you sure you want to remove this item?')) {
                this.data.splice(indexOfItemToDelete, 1);
            }
        },
        updateImage: function (index, image) {
            this.$set(this.data, index, Object.assign(this.data.blocks[index], {image_id: (image && image.id) ? image.id : null}));
        },
        typeChanged: function (type, i, index) {
            this.data.blocks[index].answers[i].type = type;
            type == 'Freetext' ? this.data.blocks[index].answers[i].availability = null : this.data.blocks[index].answers[i].text = null;
        },
        availabilityChanged: function (availability, i, index) {
            this.data.blocks[index].answers[i].availability = availability;
        },
        pickChanged: function (pick, index) {
            var toggle = (pick) ? "0" : "1";
            this.data.blocks = this.data.blocks.map((item, i) => { (i == index) ? item.picked = pick : item.picked = toggle; return item; });
        },
        updateCategory: function (index, category) {
            this.$set(this.data, index, Object.assign(this.data.blocks[index], {category_id: (category && category.id) ? category.id : null}));
        },
    },
    watch: {
        value: function (newValue) {
            this.data = newValue;
        }
    }
};
</script>
